import { create } from 'zustand';

/** Событие Chrome/Edge/Android: браузер готов предложить установку. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** Браузер отдал событие — можно показать свою кнопку «Установить». */
  prompt: BeforeInstallPromptEvent | null;
  /** Открыто как установленное приложение, а не вкладка. */
  standalone: boolean;
}

/** Открыто ли с домашнего экрана. iOS — своё свойство navigator. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Установка приложения на устройство.
 *
 * Chrome и Android перед своим баннером шлют beforeinstallprompt; событие
 * перехватывается и хранится, чтобы установка началась по нашей кнопке в
 * профиле, а не по баннеру браузера. Safari (iOS и Mac) события не даёт:
 * там установка — только руками через «Поделиться», и профиль это
 * подсказывает текстом.
 */
export const useInstall = create<InstallState>(() => ({
  prompt: null,
  standalone: isStandalone(),
}));

/** Ставит слушатели один раз, на старте приложения. */
export function installPromptTracker(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    useInstall.setState({ prompt: event as BeforeInstallPromptEvent });
  });

  window.addEventListener('appinstalled', () => {
    useInstall.setState({ prompt: null, standalone: true });
  });
}

/** Показывает системный диалог установки; true — человек согласился. */
export async function promptInstall(): Promise<boolean> {
  const { prompt } = useInstall.getState();
  if (prompt === null) {
    return false;
  }

  await prompt.prompt();

  const { outcome } = await prompt.userChoice;

  // Событие одноразовое: после ответа браузер его не повторит до перезагрузки.
  useInstall.setState({ prompt: null });

  return outcome === 'accepted';
}

/** Safari на iPhone/iPad: установка только через «Поделиться». */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

/**
 * Не давать iOS приближать страницу при касании поля ввода.
 *
 * Safari на iPhone увеличивает страницу, стоит тронуть поле со шрифтом
 * мельче 16px, — и после каждого ввода экран остаётся увеличенным. Это
 * гасится только `maximum-scale=1` в viewport; щипок при этом на iOS
 * работает по-прежнему (Apple с iOS 10 игнорирует запрет масштаба ради
 * доступности, но приближение при фокусе им всё же выключается). На
 * Android тот же атрибут отключил бы щипок совсем, а фокус там ничего не
 * приближает — поэтому только iOS и только скриптом.
 */
export function preventIOSInputZoom(): void {
  if (!isIOS()) {
    return;
  }

  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');

  if (meta !== null && !meta.content.includes('maximum-scale')) {
    meta.content = `${meta.content}, maximum-scale=1`;
  }
}
